import React from "react";
import { Calendar, MessageCircle, Phone, ShieldCheck, Award, Stamp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CONTACT, YOHALY_HERO_PHOTO } from "@/lib/company";

export default function Hero() {
  const scrollTo = (h) => document.querySelector(h)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#0B2D5C]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-[#C89B2C]/5 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 reveal">
          <div className="flex flex-wrap gap-2 mb-6">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0B2D5C]/8 border border-[#0B2D5C]/15 text-xs font-semibold text-[#0B2D5C] tracking-[0.1em] uppercase">
              <ShieldCheck size={12} strokeWidth={2} />
              Notary Public FL
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#C89B2C]/12 border border-[#C89B2C]/30 text-xs font-semibold text-[#5E4515] tracking-[0.1em] uppercase">
              <Award size={12} strokeWidth={2} />
              IRS Certified
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0f172a]/8 border border-[#0f172a]/15 text-xs font-semibold text-[#0f172a] tracking-[0.1em] uppercase">
              <Stamp size={12} strokeWidth={2} />
              CAA · ITIN Agent
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0f172a] leading-[1.05]">
            Soluciones profesionales para{" "}
            <span className="text-[#0B2D5C]">tus impuestos</span>, tu negocio y{" "}
            <span className="relative inline-block">
              <span className="relative z-10">tu tranquilidad.</span>
              <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#C89B2C]/40 -z-0"></span>
            </span>
          </h1>

          <p className="mt-6 text-lg text-[#475569] leading-relaxed max-w-2xl">
            En <strong className="text-[#0f172a]">Great Solutions US</strong> ayudamos a personas, familias y
            empresarios con soluciones tributarias, empresariales, notariales, migratorias y de seguros, brindando
            atención personalizada con ética, transparencia y compromiso.
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <Button
              onClick={() => scrollTo("#contacto")}
              className="bg-[#0B2D5C] hover:bg-[#081F42] text-white rounded-sm h-12 px-6 text-base"
              data-testid="hero-cta-primary"
            >
              <Calendar size={18} className="mr-2" />
              Agenda tu consulta
            </Button>
            <a
              href={`https://wa.me/${CONTACT.whatsapp}?text=Hola%20Yohaly%2C%20me%20gustar%C3%ADa%20agendar%20una%20consulta`}
              target="_blank"
              rel="noreferrer"
              data-testid="hero-cta-whatsapp"
            >
              <Button className="bg-[#25D366] hover:bg-[#128C7E] text-white rounded-sm h-12 px-6 text-base">
                <MessageCircle size={18} className="mr-2" />
                WhatsApp
              </Button>
            </a>
            <a href={`tel:${CONTACT.phone1Tel}`} data-testid="hero-cta-phone">
              <Button
                variant="outline"
                className="border-[#0f172a] text-[#0f172a] hover:bg-[#0f172a] hover:text-white rounded-sm h-12 px-6 text-base"
              >
                <Phone size={16} className="mr-2" />
                Llámanos ahora
              </Button>
            </a>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-[#475569]">
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-[#0B2D5C]" strokeWidth={1.75} />
              <span>Servicio confidencial</span>
            </div>
            <div className="flex items-center gap-2">
              <Award size={16} className="text-[#C89B2C]" strokeWidth={1.75} />
              <span>+10 años de experiencia</span>
            </div>
            <div className="flex items-center gap-2">
              <MessageCircle size={16} className="text-[#0B2D5C]" strokeWidth={1.75} />
              <span>Bilingüe · Español / English</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-5 reveal">
          <div className="relative">
            <div className="relative rounded-sm overflow-hidden border border-[#e2e8f0] shadow-2xl">
              <img
                src={YOHALY_HERO_PHOTO}
                alt="Yohaly Acevedo - Fundadora Great Solutions US"
                className="w-full h-[560px] object-cover object-center"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050F26]/60 via-transparent to-transparent"></div>

              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm border border-white p-5 rounded-sm shadow-lg">
                <div className="text-[10px] font-semibold text-[#0B2D5C] tracking-[0.2em] uppercase mb-1">
                  Fundadora
                </div>
                <div className="text-xl font-display font-bold text-[#0f172a]">
                  Yohaly Acevedo
                </div>
                <div className="text-xs text-[#475569] mt-1">
                  Notary Public FL · IRS Certified · CAA
                </div>
              </div>
            </div>

            <div className="absolute -top-4 -left-4 bg-[#C89B2C] text-[#0f172a] px-4 py-2 rounded-sm shadow-lg">
              <div className="text-[10px] font-bold tracking-[0.15em] uppercase">Certificada</div>
              <div className="text-sm font-semibold">Estado de Florida</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
